import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Result, Space, Spin, Tooltip, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  MailOutlined,
  PrinterOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import endpoints from '../../api/endpoints';
import client from '../../api/client';
import { notifyError } from '../../utils/notify';
import ReportJobStatus from '../../components/reports/ReportJobStatus';
import ReportEmailModal from '../../components/reports/ReportEmailModal';

// Stage R3 viewer rework — Crystal-Reports-style viewer.
//
// Behavioural contract:
//   * Arrives via /reports/viewer/:jobId (always a fresh job — the
//     button that creates it does NOT reuse an old completed jobId).
//   * Polls ONLY this jobId (never the broader /jobs list). Polling
//     stops on terminal status, on unmount, and after the absolute
//     timeout below (which surfaces a friendly error rather than
//     spinning forever).
//   * Single-row Ant Design toolbar:
//       LEFT  — Back, title + status pill
//       RIGHT — Refresh, Print, Email
//     Wraps responsively (Space `wrap`) so narrow screens stack the
//     trailing tools onto a second line without breaking layout.
//   * Body = a centered "printable page" iframe over the
//     /api/reports/jobs/:id/result HTML — same srcDoc/sandbox
//     pattern as the legacy viewer so the print dialog continues
//     to use the embedded @page CSS.
//
// Out-of-scope (intentionally): job history, "back to reports list",
// re-running by selecting from a list. The trigger lives wherever
// the user clicked "View report" — coming back is the browser Back.

const POLL_MS = 2_500;
// Hard ceiling on polling — 5 minutes is several times the 99th
// percentile render time we've seen. After this we stop polling and
// show a "still queued" message; the user can refresh manually.
const POLL_TIMEOUT_MS = 5 * 60_000;
const ACTIVE = new Set(['queued', 'running']);
const FIT_WIDTH_TARGET = 1.0;

export default function ReportEngineViewerPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [job, setJob]     = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | active | ready | error | timed_out
  const [error, setError] = useState(null);

  const [html, setHtml]       = useState(null);
  const [pdfUrl, setPdfUrl]   = useState(null);       // object URL for a PDF-format job
  const [htmlLoading, setHtmlLoading] = useState(false);
  const [htmlError, setHtmlError]     = useState(null);
  const [refreshKey, setRefreshKey]   = useState(0);
  const pdfUrlRef = useRef(null);

  const [emailOpen, setEmailOpen] = useState(false);
  // Zoom controls were removed from the toolbar; the on-screen HTML page
  // renders at a fixed 100% (PDF jobs use the native PDF viewer's own zoom).
  const zoom = FIT_WIDTH_TARGET;

  const iframeRef = useRef(null);
  const pollTimerRef = useRef(null);
  const abortRef   = useRef(null);
  const pollStartedAtRef = useRef(0);
  const failureNotifiedRef = useRef(false);

  // ---- Polling (stop on terminal, unmount, or absolute timeout) ----
  useEffect(() => {
    let cancelled = false;
    pollStartedAtRef.current = Date.now();
    failureNotifiedRef.current = false;

    async function pollOnce() {
      if (cancelled) return;
      try {
        const out = await endpoints.reports.getJob(jobId);
        if (cancelled) return;
        const j = out && out.job;
        setJob(j);
        if (j && ACTIVE.has(j.status)) {
          if (Date.now() - pollStartedAtRef.current > POLL_TIMEOUT_MS) {
            setPhase('timed_out');
            return;
          }
          setPhase('active');
          pollTimerRef.current = setTimeout(pollOnce, POLL_MS);
        } else if (j && j.status === 'completed') {
          setPhase('ready');
        } else {
          // failed / cancelled / expired — viewer renders a Result
          // block AND surfaces an Ant Design error notification so a
          // user looking at a different tab notices the failure.
          // Guarded by a ref so we only ping once per job.
          setPhase('ready');
          if (j && j.status === 'failed' && !failureNotifiedRef.current) {
            failureNotifiedRef.current = true;
            const code = j.errorCode || 'REPORT_RENDER_FAILED';
            const msg  = (j.errorMessage || 'The report could not be generated.')
              .toString().slice(0, 240);
            notifyError(
              { response: { data: { error: { code, message: msg } } } },
              `[${code}] ${msg}`
            );
          }
        }
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[ReportEngineViewer] poll failed', err);
        setError(err);
        setPhase('error');
      }
    }
    pollOnce();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [jobId]);

  // Revoke any live PDF object URL on unmount.
  useEffect(() => () => {
    if (pdfUrlRef.current) { URL.revokeObjectURL(pdfUrlRef.current); pdfUrlRef.current = null; }
  }, []);

  // ---- Result body fetch (only after the job reports completed) ----
  // HTML jobs → text into an iframe srcDoc; PDF jobs → a Blob object URL
  // into an iframe src (native PDF view, exact page breaks on print).
  useEffect(() => {
    if (!job || job.status !== 'completed') return undefined;
    const isPdf = job.outputFormat === 'pdf' || job.format === 'pdf';
    let cancelled = false;
    setHtmlLoading(true);
    setHtmlError(null);
    setHtml(null);
    if (pdfUrlRef.current) { URL.revokeObjectURL(pdfUrlRef.current); pdfUrlRef.current = null; }
    setPdfUrl(null);

    // Use an AbortController so unmounting / refresh-key change
    // cancels an in-flight HTTP read instead of leaking a setState
    // call onto an unmounted component.
    const ac = new AbortController();
    abortRef.current = ac;

    client
      .get(endpoints.reports.resultUrl(jobId), {
        responseType: isPdf ? 'blob' : 'text',
        signal: ac.signal
      })
      .then((resp) => {
        if (cancelled) return;
        if (isPdf) {
          const blob = resp.data instanceof Blob
            ? resp.data
            : new Blob([resp.data], { type: 'application/pdf' });
          const objUrl = URL.createObjectURL(blob);
          pdfUrlRef.current = objUrl;
          setPdfUrl(objUrl);
        } else {
          const body = typeof resp.data === 'string' ? resp.data : '';
          setHtml(body);
        }
        setHtmlLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // Axios surfaces user-aborted as ERR_CANCELED — silent.
        if (err && (err.name === 'CanceledError' || err.code === 'ERR_CANCELED')) return;
        // eslint-disable-next-line no-console
        console.warn('[ReportEngineViewer] result fetch failed', err);
        setHtmlError(err);
        setHtmlLoading(false);
      });

    return () => {
      cancelled = true;
      try { ac.abort(); } catch (_e) { /* noop */ }
    };
  }, [job, jobId, refreshKey]);

  // ---- Toolbar handlers ----
  const handleBack = useCallback(() => {
    // -1 sends the user back to whatever page launched the report
    // (invoice list / detail / future ERP module). Using
    // navigate(-1) keeps that flexible — the viewer never assumes
    // "/reports" exists as a list page anymore.
    navigate(-1);
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const handlePrint = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) {
      notifyError(
        new Error('print failed'),
        'Could not open the print dialog. Try the browser menu → Print.'
      );
      return;
    }
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (_e) {
      notifyError(
        new Error('print failed'),
        'Could not open the print dialog. Try the browser menu → Print.'
      );
    }
  }, []);

  // ---- Render ----
  if (phase === 'loading') {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin tip="Opening report…" />
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <Result
        status="error"
        title="Could not open this report"
        subTitle={
          error && error.response && error.response.data && error.response.data.error
            ? `[${error.response.data.error.code || 'ERROR'}] ${error.response.data.error.message || ''}`
            : 'Please go back and try again.'
        }
        extra={<Button onClick={handleBack}>Back</Button>}
      />
    );
  }

  if (phase === 'timed_out') {
    return (
      <Result
        status="info"
        title="This report is taking longer than usual"
        subTitle="The report is still being generated. Click Refresh in a moment to check again, or go back and try again."
        extra={
          <Space>
            <Button onClick={handleBack}>Back</Button>
            <Button type="primary" icon={<ReloadOutlined />} onClick={() => {
              setPhase('loading');
              setError(null);
              pollStartedAtRef.current = Date.now();
              // Re-trigger the polling effect by bumping refreshKey.
              setRefreshKey((k) => k + 1);
            }}>
              Refresh
            </Button>
          </Space>
        }
      />
    );
  }

  // phase === 'active' or 'ready'
  const status = (job && job.status) || 'queued';
  const reportTitle = (job && (job.reportName || job.reportCode)) || 'Report';

  return (
    <div className="gi-report-engine-viewer">
      <Toolbar
        title={reportTitle}
        status={status}
        onBack={handleBack}
        onRefresh={handleRefresh}
        onPrint={handlePrint}
        onEmail={() => setEmailOpen(true)}
        printDisabled={status !== 'completed'}
        emailDisabled={status !== 'completed'}
      />

      <ReportBody
        status={status}
        job={job}
        html={html}
        pdfUrl={pdfUrl}
        isPdf={!!(job && (job.outputFormat === 'pdf' || job.format === 'pdf'))}
        htmlLoading={htmlLoading}
        htmlError={htmlError}
        zoom={zoom}
        iframeRef={iframeRef}
      />

      <ReportEmailModal
        open={emailOpen}
        jobId={jobId}
        onClose={() => setEmailOpen(false)}
      />
    </div>
  );
}

// -------------------------------------------------------------------
// Single-row toolbar. LEFT and RIGHT are independent <Space wrap>
// groups inside a flex container so the trailing tools wrap onto a
// second line on narrow viewports without dragging the title with
// them. All buttons share `size="middle"` for visual consistency.
// -------------------------------------------------------------------
function Toolbar({
  title, status, onBack, onRefresh, onPrint, onEmail,
  printDisabled, emailDisabled
}) {
  return (
    <div
      className="gi-report-toolbar"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 16px',
        background: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        position: 'sticky',
        top: 0,
        zIndex: 5
      }}
    >
      <Space size="small" wrap align="center">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          aria-label="Back"
        >
          Back
        </Button>
        <Typography.Text strong style={{ fontSize: 14 }}>
          {title}
        </Typography.Text>
        <ReportJobStatus status={status} />
      </Space>

      <Space size="small" wrap align="center">
        <Tooltip title="Refresh">
          <Button icon={<ReloadOutlined />} onClick={onRefresh} aria-label="Refresh" />
        </Tooltip>
        <Tooltip title="Print">
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={onPrint}
            disabled={printDisabled}
          >
            Print
          </Button>
        </Tooltip>
        <Tooltip title="Email this report">
          <Button
            icon={<MailOutlined />}
            onClick={onEmail}
            disabled={emailDisabled}
          >
            Email
          </Button>
        </Tooltip>
      </Space>
    </div>
  );
}

// -------------------------------------------------------------------
// Body — centered printable page area below the toolbar. Renders one
// of:
//   * "running" spinner with the live status pill
//   * Result for failed / cancelled / expired
//   * The HTML iframe for completed
// -------------------------------------------------------------------
function ReportBody({
  status, job, html, pdfUrl, isPdf, htmlLoading, htmlError, zoom, iframeRef
}) {
  const wrapperStyle = {
    background: '#e9eef4',
    minHeight: 'calc(100vh - 120px)',
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  };

  if (status === 'queued' || status === 'running') {
    return (
      <div style={wrapperStyle}>
        <Space direction="vertical" align="center" size="large" style={{ paddingTop: 64 }}>
          <Spin size="large" />
          <Typography.Title level={4} style={{ margin: 0 }}>
            Generating your report…
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            This page refreshes automatically.
          </Typography.Paragraph>
        </Space>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div style={wrapperStyle}>
        <Result
          status="info"
          title="This report has expired"
          subTitle="Temporary report outputs are kept for a short window. Re-open the report to view it again."
        />
      </div>
    );
  }

  if (status !== 'completed') {
    const safeMsg = ((job && job.errorMessage) || '').toString().slice(0, 240);
    const heading = status === 'failed'
      ? 'The report could not be generated'
      : `Report ${status}`;
    return (
      <div style={wrapperStyle}>
        <Result
          status={status === 'failed' ? 'error' : 'info'}
          title={heading}
          subTitle={
            (job && job.errorCode)
              ? `[${job.errorCode}] ${safeMsg || 'No additional details available.'}`
              : (safeMsg || 'No additional details available.')
          }
        />
      </div>
    );
  }

  // status === 'completed'
  if (htmlLoading) {
    return (
      <div style={wrapperStyle}>
        <Spin tip="Loading report…" style={{ marginTop: 64 }} />
      </div>
    );
  }

  // PDF-format job → native PDF view (exact page breaks; Print prints the PDF).
  if (isPdf) {
    if (htmlError || !pdfUrl) {
      return (
        <div style={wrapperStyle}>
          <Result
            status="warning"
            title="Could not load the report content"
            subTitle="Click Refresh in the toolbar to try again."
          />
        </div>
      );
    }
    return (
      <div style={wrapperStyle}>
        <div style={{ width: '100%', maxWidth: 1024, background: 'transparent' }}>
          <iframe
            ref={iframeRef}
            title="Glass-i Report (PDF)"
            src={pdfUrl}
            style={{
              width:      '100%',
              height:     'calc(100vh - 170px)',
              border:     'none',
              background: '#ffffff',
              display:    'block'
            }}
          />
        </div>
      </div>
    );
  }

  if (htmlError || !html) {
    return (
      <div style={wrapperStyle}>
        <Result
          status="warning"
          title="Could not load the report content"
          subTitle="Click Refresh in the toolbar to try again."
        />
      </div>
    );
  }

  // Centered printable A4-ish page area. The iframe itself carries
  // the @page A4 rules emitted by the report-service (Stage 5N-9A);
  // the wrapper just gives the on-screen viewer its centered card.
  return (
    <div style={wrapperStyle}>
      <div
        style={{
          width: '100%',
          maxWidth: 1024,
          background: 'transparent',
          overflow: 'auto'
        }}
      >
        <iframe
          ref={iframeRef}
          title="Glass-i Report"
          srcDoc={html}
          sandbox="allow-same-origin allow-modals"
          style={{
            width:           `${100 / zoom}%`,
            height:          `${1100 / zoom}px`,
            border:          'none',
            transform:       `scale(${zoom})`,
            transformOrigin: 'top left',
            background:      'transparent',
            display:         'block'
          }}
        />
      </div>
    </div>
  );
}
